// tarotCardsReverse.js
// Final complete 78-card Tarot deck.
// Includes upright and reversed meanings.
// Cleaned, ordered (0-77), duplicates removed from original source.
// Missing Swords 6-10 integrated.
// Data integrity from original sources maintained.

const tarotCards = [
  // Major Arcana (0-21)
  {
    id: 0,
    name: "The Seeker", // Often The Fool
    meaning: "The Seeker: New beginnings, spontaneity, trust, risk-taking",
    reversedMeaning:
      "Reversed: Holding back, recklessness, risk-aversion, fear of the unknown, naivety",
    theme: ["beginnings", "freedom", "potential"],
    image: "/images/fool.jpg",
    narrative: {
      past: "You began this path with a sense of wonder — unshaped by doubt, driven by instinct. That courage still lives within you.",
      present:
        "You are standing at a threshold. The next step may be uncertain, but your spirit is ready. Trust your own leap.",
      future:
        "A new journey awaits. The Seeker invites you to let go of fear and follow the pull of something wild and true.",
    },
    astrology: {
      zodiacSign: null,
      planet: "Uranus",
      element: "Air",
      modality: null,
    },
    qabalah: { path: 11, hebrewLetter: "Aleph", gematria: 1 },
  },
  {
    id: 1,
    name: "The Magician",
    meaning:
      "The Magician: Manifestation, resourcefulness, power, inspired action",
    reversedMeaning:
      "Reversed: Manipulation, poor planning, untapped talents, misuse of power, lack of focus",
    theme: ["manifestation", "power", "action"],
    image: "/images/magician.jpg",
    narrative: {
      past: "You've tapped into your abilities before — remembering this power helps you manifest again.",
      present:
        "The Magician calls you to focus and act with intention — your resources are aligned.",
      future:
        "A chance to manifest something great is coming. Your vision and tools will be key.",
    },
    astrology: {
      zodiacSign: "Gemini",
      planet: "Mercury",
      element: "Air",
      modality: "Mutable",
    },
    qabalah: { path: 12, hebrewLetter: "Beth", gematria: 2 },
  },
  {
    id: 2,
    name: "The High Priestess",
    meaning:
      "The High Priestess: Intuition, sacred knowledge, divine feminine, mystery",
    reversedMeaning:
      "Reversed: Secrets, disconnected from intuition, hidden agendas, withdrawal, silence",
    theme: ["intuition", "mystery", "knowledge"],
    image: "/images/high_priestess.jpg",
    narrative: {
      past: "You've relied on inner wisdom to navigate unseen truths — that insight still protects you.",
      present:
        "Now is the time to listen more than speak. What feels hidden will soon be revealed.",
      future:
        "You'll be called to trust your instincts — answers lie in the space between the obvious.",
    },
    astrology: {
      zodiacSign: null,
      planet: "Moon",
      element: "Water",
      modality: null,
    },
    qabalah: { path: 13, hebrewLetter: "Gimel", gematria: 3 },
  },
  {
    id: 3,
    name: "The Empress",
    meaning: "The Empress: Fertility, nurturing, abundance, beauty",
    reversedMeaning:
      "Reversed: Creative block, dependence, smothering, emptiness, lack of self-care",
    theme: ["nurturing", "abundance", "growth"],
    image: "/images/empress.jpg",
    narrative: {
      past: "You've created or supported growth in meaningful ways — and that nurturing energy still flows.",
      present:
        "You're being asked to lean into creativity, care, and connection. It's time to receive.",
      future:
        "Abundance and creation are near — whether in life, relationships, or ideas. Make space for what's growing.",
    },
    astrology: {
      zodiacSign: null,
      planet: "Venus",
      element: "Earth",
      modality: null,
    },
    qabalah: { path: 14, hebrewLetter: "Daleth", gematria: 4 },
  },
  {
    id: 4,
    name: "The Emperor",
    meaning: "The Emperor: Structure, authority, stability, fatherhood",
    reversedMeaning:
      "Reversed: Domination, excessive control, rigidity, lack of discipline, misuse of power",
    theme: ["structure", "authority", "stability"],
    image: "/images/emperor.jpg",
    narrative: {
      past: "You've built foundations that shaped your life — whether through discipline or control.",
      present:
        "Now is a time to step into leadership or reclaim order — structure serves your growth.",
      future:
        "You may soon be in a role where stability and decision-making are essential. Lead with clarity.",
    },
    astrology: {
      zodiacSign: "Aries",
      planet: "Mars",
      element: "Fire",
      modality: "Cardinal",
    },
    qabalah: { path: 15, hebrewLetter: "Heh", gematria: 5 },
  },
  {
    id: 5,
    name: "The Hierophant",
    meaning:
      "The Hierophant: Tradition, spiritual guidance, conformity, beliefs",
    reversedMeaning:
      "Reversed: Rebellion, personal beliefs, freedom, challenging tradition, unconventionality",
    theme: ["tradition", "guidance", "spirituality"],
    image: "/images/hierophant.jpg",
    narrative: {
      past: "You've followed teachings or traditions that shaped your beliefs. What still serves you?",
      present:
        "This moment calls for reflection, learning, or mentorship. Seek meaning through wisdom.",
      future:
        "You may soon be called to teach or lead with spiritual insight. Honor what you pass on.",
    },
    astrology: {
      zodiacSign: "Taurus",
      planet: "Venus",
      element: "Earth",
      modality: "Fixed",
    },
    qabalah: { path: 16, hebrewLetter: "Vau", gematria: 6 },
  },
  {
    id: 6,
    name: "The Lovers",
    meaning:
      "The Lovers: Love, harmony, relationships, values alignment, choices",
    reversedMeaning:
      "Reversed: Self-love, disharmony, imbalance, misalignment of values, conflict",
    theme: ["love", "relationships", "choice"],
    image: "/images/lovers.jpg",
    narrative: {
      past: "You've been shaped by choices of the heart — their echoes still influence your path.",
      present:
        "Now is a time to align your values with your relationships. Connection matters.",
      future:
        "A meaningful relationship or choice is on the horizon. Let love be your compass.",
    },
    astrology: {
      zodiacSign: "Gemini",
      planet: "Mercury",
      element: "Air",
      modality: "Mutable",
    },
    qabalah: { path: 17, hebrewLetter: "Zayin", gematria: 7 },
  },
  {
    id: 7,
    name: "The Chariot",
    meaning: "The Chariot: Willpower, control, victory, determination",
    reversedMeaning:
      "Reversed: Self-discipline, opposition, lack of direction, lack of control, aggression",
    theme: ["determination", "victory", "control"],
    image: "/images/chariot.jpg",
    narrative: {
      past: "You've overcome obstacles through sheer will. That drive helped shape who you are.",
      present:
        "You are in motion — your focus and discipline are pushing you forward.",
      future:
        "A challenge will soon require your resolve. Victory is yours if you stay the course.",
    },
    astrology: {
      zodiacSign: "Cancer",
      planet: "Moon",
      element: "Water",
      modality: "Cardinal",
    },
    qabalah: { path: 18, hebrewLetter: "Cheth", gematria: 8 },
  },
  {
    id: 8,
    name: "Strength",
    meaning: "Strength: Courage, persuasion, influence, compassion",
    reversedMeaning:
      "Reversed: Inner strength, self-doubt, weakness, insecurity, lack of confidence",
    theme: ["strength", "courage", "patience"],
    image: "/images/strength.jpg",
    narrative: {
      past: "You've endured with grace — your inner strength has brought you far.",
      present:
        "Now is a time to lead with compassion and courage. Quiet power is true power.",
      future:
        "You will be asked to meet a moment with heart. Strength lies in kindness and resolve.",
    },
    astrology: {
      zodiacSign: "Leo",
      planet: "Sun",
      element: "Fire",
      modality: "Fixed",
    },
    qabalah: { path: 19, hebrewLetter: "Teth", gematria: 9 },
  },
  {
    id: 9,
    name: "The Hermit",
    meaning:
      "The Hermit: Soul-searching, introspection, being alone, inner guidance",
    reversedMeaning:
      "Reversed: Isolation, loneliness, withdrawal, paranoia, becoming anti-social",
    theme: ["solitude", "introspection", "wisdom"],
    image: "/images/hermit.jpg",
    narrative: {
      past: "You've taken time to reflect deeply — that inner light still guides you.",
      present:
        "Withdraw from the noise and reconnect with your truth. The answers are within.",
      future:
        "Solitude will offer clarity. Don't be afraid to walk your own path for a while.",
    },
    astrology: {
      zodiacSign: "Virgo",
      planet: "Mercury",
      element: "Earth",
      modality: "Mutable",
    },
    qabalah: { path: 20, hebrewLetter: "Yod", gematria: 10 },
  },
  {
    id: 10,
    name: "Wheel of Fortune",
    meaning: "Wheel of Fortune: Cycles, fate, karma, turning point",
    reversedMeaning:
      "Reversed: Bad luck, resistance to change, breaking cycles, stagnation, unforeseen setbacks",
    theme: ["change", "fate", "cycles"],
    image: "/images/wheel_of_fortune.jpg",
    narrative: {
      past: "You've been part of a turning cycle — a moment that changed your direction.",
      present:
        "Something beyond your control is in motion. Trust the wheel is turning as it should.",
      future:
        "A shift is coming. Be ready to pivot and ride the rhythm of change.",
    },
    astrology: {
      zodiacSign: null,
      planet: "Jupiter",
      element: "Fire",
      modality: null,
    },
    qabalah: { path: 21, hebrewLetter: "Kaph", gematria: 20 },
  },
  {
    id: 11,
    name: "Justice",
    meaning: "Justice: Fairness, truth, law, cause and effect",
    reversedMeaning:
      "Reversed: Unfairness, lack of accountability, dishonesty, prejudice, legal tangles",
    theme: ["truth", "balance", "justice"],
    image: "/images/justice.jpg",
    narrative: {
      past: "Past decisions or truths still weigh in. Integrity has shaped your current path.",
      present:
        "This is a time to face facts and stay aligned with your values.",
      future:
        "A decision will bring clarity or closure. Let fairness guide your hand.",
    },
    astrology: {
      zodiacSign: "Libra",
      planet: "Venus",
      element: "Air",
      modality: "Cardinal",
    },
    qabalah: { path: 22, hebrewLetter: "Lamed", gematria: 30 },
  },
  {
    id: 12,
    name: "The Hanged Man",
    meaning: "The Hanged Man: Letting go, perspective, pause, surrender",
    reversedMeaning:
      "Reversed: Delays, resistance, stalling, indecision, needless sacrifice",
    theme: ["sacrifice", "reflection", "release"],
    image: "/images/hanged_man.jpg",
    narrative: {
      past: "You've paused before, chosen surrender, or let go of something dear. That decision reshaped you.",
      present:
        "This moment calls for patience and a new perspective — trust the stillness.",
      future:
        "You may soon be asked to pause, reflect, or see things from a different view before moving forward.",
    },
    astrology: {
      zodiacSign: null,
      planet: "Neptune",
      element: "Water",
      modality: null,
    },
    qabalah: { path: 23, hebrewLetter: "Mem", gematria: 40 },
  },
  {
    id: 13,
    name: "Death",
    meaning: "Death: Endings, transformation, transition, rebirth",
    reversedMeaning:
      "Reversed: Resistance to change, fear of endings, stagnation, repeating patterns, holding on",
    theme: ["endings", "transformation", "rebirth"],
    image: "/images/death.jpg",
    narrative: {
      past: "Something ended — and while painful, it cleared the way for new growth.",
      present:
        "You're in a moment of transformation. Let what needs to go, go.",
      future:
        "A chapter will close — not as punishment, but as a beginning in disguise.",
    },
    astrology: {
      zodiacSign: "Scorpio",
      planet: "Pluto",
      element: "Water",
      modality: "Fixed",
    },
    qabalah: { path: 24, hebrewLetter: "Nun", gematria: 50 },
  },
  {
    id: 14,
    name: "Temperance",
    meaning: "Temperance: Balance, moderation, patience, purpose",
    reversedMeaning:
      "Reversed: Imbalance, excess, extremes, lack of patience, discord",
    theme: ["balance", "moderation", "healing"],
    image: "/images/temperance.jpg",
    narrative: {
      past: "You've worked toward equilibrium — your inner balance has helped you stay grounded.",
      present:
        "Now is the time to take the middle path. Blend logic with intuition.",
      future:
        "You will soon find harmony by bringing opposing elements together.",
    },
    astrology: {
      zodiacSign: "Sagittarius",
      planet: "Jupiter",
      element: "Fire",
      modality: "Mutable",
    },
    qabalah: { path: 25, hebrewLetter: "Samekh", gematria: 60 },
  },
  {
    id: 15,
    name: "The Devil",
    meaning: "The Devil: Addiction, materialism, temptation, restriction",
    reversedMeaning:
      "Reversed: Releasing limiting beliefs, exploring dark thoughts, detachment, freedom, breaking free",
    theme: ["temptation", "materialism", "addiction"],
    image: "/images/devil.jpg",
    narrative: {
      past: "You've been entangled by something that once had power over you — maybe still does.",
      present:
        "You're being asked to examine your attachments. What's holding you back?",
      future:
        "A moment is coming where you'll need to free yourself from something limiting.",
    },
    astrology: {
      zodiacSign: "Capricorn",
      planet: "Saturn",
      element: "Earth",
      modality: "Cardinal",
    },
    qabalah: { path: 26, hebrewLetter: "Ayin", gematria: 70 },
  },
  {
    id: 16,
    name: "The Tower",
    meaning: "The Tower: Upheaval, sudden change, chaos, awakening",
    reversedMeaning:
      "Reversed: Personal transformation, fear of change, averting disaster, delaying the inevitable",
    theme: ["shock", "upheaval", "transformation"],
    image: "/images/tower.jpg",
    narrative: {
      past: "You've survived a rupture — something once solid fell away, clearing space for truth.",
      present:
        "Now may feel chaotic or uncertain. Trust that collapse can be a sacred reset.",
      future:
        "An awakening is coming. The ground may shake, but clarity will rise from it.",
    },
    astrology: {
      zodiacSign: null,
      planet: "Mars",
      element: "Fire",
      modality: null,
    },
    qabalah: { path: 27, hebrewLetter: "Peh", gematria: 80 },
  },
  {
    id: 17,
    name: "The Star",
    meaning: "The Star: Hope, faith, renewal, inspiration",
    reversedMeaning:
      "Reversed: Lack of faith, despair, discouragement, disconnection, negativity",
    theme: ["hope", "healing", "renewal"],
    image: "/images/star.jpg",
    narrative: {
      past: "You've held onto hope — even when things felt broken.",
      present:
        "This is a moment for healing, inspiration, and calm. Let light guide you.",
      future:
        "Renewed faith is on the horizon. A new dream is beginning to take form.",
    },
    astrology: {
      zodiacSign: "Aquarius",
      planet: "Uranus",
      element: "Air",
      modality: "Fixed",
    },
    qabalah: { path: 28, hebrewLetter: "Tzaddi", gematria: 90 },
  },
  {
    id: 18,
    name: "The Moon",
    meaning: "The Moon: Illusion, fear, anxiety, subconscious",
    reversedMeaning:
      "Reversed: Release of fear, repressed emotion, inner confusion, clarity, truth revealed",
    theme: ["illusion", "fear", "mystery"],
    image: "/images/moon.jpg",
    narrative: {
      past: "You've walked through confusion — the fog still may linger.",
      present:
        "Something may not be what it seems. Trust your intuition to find your way.",
      future:
        "More will be revealed soon. Don't rush clarity — feel your way forward.",
    },
    astrology: {
      zodiacSign: "Pisces",
      planet: "Neptune",
      element: "Water",
      modality: "Mutable",
    },
    qabalah: { path: 29, hebrewLetter: "Qoph", gematria: 100 },
  },
  {
    id: 19,
    name: "The Sun",
    meaning: "The Sun: Positivity, fun, warmth, success",
    reversedMeaning:
      "Reversed: Inner child, feeling down, overly optimistic, lack of success, pessimism",
    theme: ["joy", "success", "clarity"],
    image: "/images/sun.jpg",
    narrative: {
      past: "You've known joy and radiance — your light has inspired others.",
      present:
        "This is a time to celebrate wins, radiate truth, and enjoy the moment.",
      future:
        "Happiness is coming. A moment of pure clarity and confidence will soon shine through.",
    },
    astrology: {
      zodiacSign: null,
      planet: "Sun",
      element: "Fire",
      modality: null,
    },
    qabalah: { path: 30, hebrewLetter: "Resh", gematria: 200 },
  },
  {
    id: 20,
    name: "Judgement",
    meaning: "Judgement: Awakening, reckoning, calling, absolution",
    reversedMeaning:
      "Reversed: Self-doubt, inner critic, ignoring the call, fear of judgement, indecisiveness",
    theme: ["awakening", "truth", "rebirth"],
    image: "/images/judgement.jpg",
    narrative: {
      past: "You've come to powerful realizations — they're shaping your direction now.",
      present:
        "You're being asked to step into truth. Shed what no longer defines you.",
      future: "An awakening is near. This is your invitation to rise.",
    },
    astrology: {
      zodiacSign: null,
      planet: "Pluto",
      element: "Fire",
      modality: null,
    },
    qabalah: { path: 31, hebrewLetter: "Shin", gematria: 300 },
  },
  {
    id: 21,
    name: "The World",
    meaning: "The World: Completion, integration, travel, accomplishment",
    reversedMeaning:
      "Reversed: Seeking personal closure, short-cuts, delays, lack of completion, incompletion",
    theme: ["completion", "wholeness", "success"],
    image: "/images/world.jpg",
    narrative: {
      past: "You've reached significant milestones — and learned the beauty of closure.",
      present: "This is a moment of fulfillment — a cycle is complete.",
      future:
        "A sense of wholeness is on its way. You will soon step into something fully aligned.",
    },
    astrology: {
      zodiacSign: null,
      planet: "Saturn",
      element: "Earth",
      modality: null,
    },
    qabalah: { path: 32, hebrewLetter: "Tau", gematria: 400 },
  },

  // Suit of Wands (22-35)
  {
    id: 22,
    name: "Ace of Wands",
    meaning: "Ace of Wands: Inspiration, new opportunities, growth, potential",
    reversedMeaning:
      "Reversed: Lack of direction, delays, creative blocks, apathy, hesitation",
    theme: ["beginnings", "potential", "creativity"],
    image: "/images/ace_of_wands.jpg",
    narrative: {
      past: "A spark once lit your imagination — its momentum helped shape what followed.",
      present:
        "Now is the time to act on a bold idea or inner fire — something wants to begin.",
      future:
        "A flash of inspiration will arrive. Trust the urge to create or initiate.",
    },
    astrology: {
      zodiacSign: null,
      planet: null,
      element: "Fire",
      modality: null,
    },
    qabalah: { path: "Kether", hebrewLetter: null, gematria: null },
  },
  {
    id: 23,
    name: "Two of Wands",
    meaning: "Two of Wands: Future planning, progress, decisions, discovery",
    reversedMeaning:
      "Reversed: Fear of unknown, lack of planning, playing safe, delays, indecision",
    theme: ["choice", "potential", "action"],
    image: "/images/two_of_wands.jpg",
    narrative: {
      past: "You've stood at a crossroads before — the path you chose still echoes.",
      present:
        "Now is a moment to look ahead and make a conscious choice about direction.",
      future:
        "A clear opportunity will appear — be prepared to act with foresight.",
    },
    astrology: {
      zodiacSign: "Aries",
      planet: "Mars",
      element: "Fire",
      modality: "Cardinal",
    },
    qabalah: { path: "Chokmah", hebrewLetter: null, gematria: null },
  },
  {
    id: 24,
    name: "Three of Wands",
    meaning:
      "Three of Wands: Expansion, foresight, overseas opportunities, progress",
    reversedMeaning:
      "Reversed: Obstacles, delays, frustration, lack of foresight, holding back",
    theme: ["growth", "action", "potential"],
    image: "/images/three_of_wands.jpg",
    narrative: {
      past: "You planted seeds that are now taking root — expansion is underway.",
      present:
        "You're standing at the edge of what's next. Prepare to grow beyond current borders.",
      future: "New horizons await. A vision you hold will start to take shape.",
    },
    astrology: {
      zodiacSign: "Aries",
      planet: "Sun",
      element: "Fire",
      modality: "Cardinal",
    },
    qabalah: { path: "Binah", hebrewLetter: null, gematria: null },
  },
  {
    id: 25,
    name: "Four of Wands",
    meaning: "Four of Wands: Celebration, harmony, marriage, home, community",
    reversedMeaning:
      "Reversed: Lack of support, instability, transition, home conflicts, lack of harmony",
    theme: ["celebration", "connection", "stability"],
    image: "/images/four_of_wands.jpg",
    narrative: {
      past: "You've experienced joy in togetherness — that foundation still supports you.",
      present:
        "This is a time to celebrate progress, connection, or a milestone.",
      future:
        "A moment of harmony and recognition is coming — welcome it with open arms.",
    },
    astrology: {
      zodiacSign: "Aries",
      planet: "Venus",
      element: "Fire",
      modality: "Cardinal",
    },
    qabalah: { path: "Chesed", hebrewLetter: null, gematria: null },
  },
  {
    id: 26,
    name: "Five of Wands",
    meaning: "Five of Wands: Conflict, disagreements, competition, tension",
    reversedMeaning:
      "Reversed: Avoiding conflict, respect for differences, finding common ground, peace",
    theme: ["struggle", "competition", "chaos"],
    image: "/images/five_of_wands.jpg",
    narrative: {
      past: "You've navigated tension or competition — and learned how to hold your ground.",
      present:
        "Now may bring friction or disagreement. Stay centered in your truth.",
      future:
        "A test of will may arise — but it will clarify your strengths and values.",
    },
    astrology: {
      zodiacSign: "Leo",
      planet: "Saturn",
      element: "Fire",
      modality: "Fixed",
    },
    qabalah: { path: "Geburah", hebrewLetter: null, gematria: null },
  },
  {
    id: 27,
    name: "Six of Wands",
    meaning:
      "Six of Wands: Success, public recognition, progress, self-confidence",
    reversedMeaning:
      "Reversed: Private achievement, lack of recognition, failure, self-doubt, egotism",
    theme: ["victory", "success", "recognition"],
    image: "/images/six_of_wands.jpg",
    narrative: {
      past: "You've been acknowledged for something meaningful — let that success empower your next move.",
      present:
        "You're being recognized or lifted up — it's safe to feel proud.",
      future:
        "A win is coming — trust that your efforts will be seen and celebrated.",
    },
    astrology: {
      zodiacSign: "Leo",
      planet: "Jupiter",
      element: "Fire",
      modality: "Fixed",
    },
    qabalah: { path: "Tiphareth", hebrewLetter: null, gematria: null },
  },
  {
    id: 28,
    name: "Seven of Wands",
    meaning: "Seven of Wands: Challenge, competition, protection, perseverance",
    reversedMeaning:
      "Reversed: Giving up, overwhelmed, exhaustion, compromise, feeling attacked",
    theme: ["struggle", "determination", "courage"],
    image: "/images/seven_of_wands.jpg",
    narrative: {
      past: "You've stood your ground before, even when it was tough — your strength is earned.",
      present:
        "Now is a time to protect your position or belief — stay rooted.",
      future:
        "A challenge may test your resolve — but you have what it takes to hold your line.",
    },
    astrology: {
      zodiacSign: "Leo",
      planet: "Mars",
      element: "Fire",
      modality: "Fixed",
    },
    qabalah: { path: "Netzach", hebrewLetter: null, gematria: null },
  },
  {
    id: 29,
    name: "Eight of Wands",
    meaning: "Eight of Wands: Movement, fast paced change, action, alignment",
    reversedMeaning:
      "Reversed: Delays, frustration, resisting change, slowness, internal alignment needed",
    theme: ["action", "change", "speed"],
    image: "/images/eight_of_wands.jpg",
    narrative: {
      past: "A quick shift once launched change — its momentum is still unfolding.",
      present:
        "Things are accelerating — align yourself with the motion and clarity will follow.",
      future: "Expect fast progress or news. Stay flexible and ready.",
    },
    astrology: {
      zodiacSign: "Sagittarius",
      planet: "Mercury",
      element: "Fire",
      modality: "Mutable",
    },
    qabalah: { path: "Hod", hebrewLetter: null, gematria: null },
  },
  {
    id: 30,
    name: "Nine of Wands",
    meaning: "Nine of Wands: Resilience, courage, persistence, test of faith",
    reversedMeaning:
      "Reversed: Lack of conviction, paranoia, defensive, hesitant, giving up",
    theme: ["strength", "resilience", "patience"],
    image: "/images/nine_of_wands.jpg",
    narrative: {
      past: "You've carried burdens with determination — your survival is a triumph.",
      present:
        "You're near the end of something hard — stay strong, the finish line is close.",
      future:
        "One more push is needed. Protect your energy and trust your strength.",
    },
    astrology: {
      zodiacSign: "Sagittarius",
      planet: "Moon",
      element: "Fire",
      modality: "Mutable",
    },
    qabalah: { path: "Yesod", hebrewLetter: null, gematria: null },
  },
  {
    id: 31,
    name: "Ten of Wands",
    meaning: "Ten of Wands: Burden, responsibility, hard work, stress",
    reversedMeaning:
      "Reversed: Doing it all, carrying the burden, delegation, release, collapse",
    theme: ["struggle", "burden", "completion"],
    image: "/images/ten_of_wands.jpg",
    narrative: {
      past: "You've taken on too much before — that weight left an imprint.",
      present: "You're carrying a heavy load. Consider what can be released.",
      future:
        "A release is coming. But first, complete the journey you committed to.",
    },
    astrology: {
      zodiacSign: "Sagittarius",
      planet: "Saturn",
      element: "Fire",
      modality: "Mutable",
    },
    qabalah: { path: "Malkuth", hebrewLetter: null, gematria: null },
  },
  {
    id: 32,
    name: "Page of Wands",
    meaning:
      "Page of Wands: Enthusiasm, exploration, new ideas, creative potential",
    reversedMeaning:
      "Reversed: Lack of direction, procrastination, creating conflict, pessimism, self-limiting beliefs",
    theme: ["creativity", "beginnings", "exploration"],
    image: "/images/page_of_wands.jpg",
    narrative: {
      past: "A curious spark once called you forward — and your journey began with questions.",
      present:
        "This is a moment for inspiration and risk — don't be afraid to explore.",
      future:
        "A message or bold idea is coming — let curiosity guide your next steps.",
    },
    astrology: {
      zodiacSign: null,
      planet: null,
      element: "Earth of Fire",
      modality: null,
    },
    qabalah: { path: null, hebrewLetter: null, gematria: null },
  },
  {
    id: 33,
    name: "Knight of Wands",
    meaning: "Knight of Wands: Action, adventure, passion, boldness",
    reversedMeaning:
      "Reversed: Anger, impulsiveness, recklessness, delays, frustration",
    theme: ["action", "adventure", "passion"],
    image: "/images/knight_of_wands.jpg",
    narrative: {
      past: "You've leapt toward what you desired — and your fire left a trail.",
      present:
        "Now is a time for fearless movement — act with passion and purpose.",
      future: "A new adventure calls — follow your flame, but mind the risks.",
    },
    astrology: {
      zodiacSign: null,
      planet: null,
      element: "Air of Fire",
      modality: null,
    },
    qabalah: { path: null, hebrewLetter: null, gematria: null },
  },
  {
    id: 34,
    name: "Queen of Wands",
    meaning: "Queen of Wands: Confidence, warmth, determination, inspiration",
    reversedMeaning:
      "Reversed: Selfishness, jealousy, demands, insecurity, lack of confidence",
    theme: ["confidence", "creativity", "power"],
    image: "/images/queen_of_wands.jpg",
    narrative: {
      past: "You've inspired others with your presence — your fire draws people in.",
      present:
        "Step boldly — now is the time to express your full creative self.",
      future:
        "An opportunity to lead with charisma and confidence is on the horizon.",
    },
    astrology: {
      zodiacSign: null,
      planet: null,
      element: "Water of Fire",
      modality: null,
    },
    qabalah: { path: null, hebrewLetter: null, gematria: null },
  },
  {
    id: 35,
    name: "King of Wands",
    meaning: "King of Wands: Leadership, vision, long-term success, drive",
    reversedMeaning:
      "Reversed: Impulsiveness, haste, ruthless, high expectations, tyrannical",
    theme: ["vision", "leadership", "success"],
    image: "/images/king_of_wands.jpg",
    narrative: {
      past: "You've commanded respect through bold vision — your fire shaped paths.",
      present:
        "This moment calls for decisive leadership — claim your influence.",
      future:
        "A chance to build something lasting will emerge — lead with passion and strategy.",
    },
    astrology: {
      zodiacSign: null,
      planet: null,
      element: "Fire of Fire",
      modality: null,
    },
    qabalah: { path: null, hebrewLetter: null, gematria: null },
  },

  // Suit of Cups (36-49)
  {
    id: 36,
    name: "Ace of Cups",
    meaning: "Ace of Cups: Love, compassion, creativity, overwhelming emotion",
    reversedMeaning:
      "Reversed: Self-love, intuition blocked, repressed emotions, emptiness",
    theme: ["love", "creativity", "beginnings"],
    image: "/images/ace_of_cups.jpg",
    narrative: {
      past: "An emotional spark once filled you — that open heart still guides you.",
      present:
        "This is a time of emotional openness and potential for new connection.",
      future: "A fresh emotional chapter is approaching — let it flow freely.",
    },
    astrology: {
      zodiacSign: null,
      planet: null,
      element: "Water",
      modality: null,
    },
    qabalah: { path: "Kether", hebrewLetter: null, gematria: null },
  },
  {
    id: 37,
    name: "Two of Cups",
    meaning:
      "Two of Cups: Unified love, partnership, attraction, relationships",
    reversedMeaning:
      "Reversed: Self-love, break-ups, disharmony, distrust, imbalance",
    theme: ["love", "connection", "relationships"],
    image: "/images/two_of_cups.jpg",
    narrative: {
      past: "A heartfelt bond helped shape your emotional foundation.",
      present:
        "Connection is forming or deepening — let love meet you halfway.",
      future:
        "A powerful partnership or emotional union is coming into alignment.",
    },
    astrology: {
      zodiacSign: "Cancer",
      planet: "Venus",
      element: "Water",
      modality: "Cardinal",
    },
    qabalah: { path: "Chokmah", hebrewLetter: null, gematria: null },
  },
  {
    id: 38,
    name: "Three of Cups",
    meaning: "Three of Cups: Celebration, friendship, creativity, community",
    reversedMeaning:
      "Reversed: Independence, alone time, hardcore partying, 'three's a crowd', scandals",
    theme: ["celebration", "connection", "joy"],
    image: "/images/three_of_cups.jpg",
    narrative: {
      past: "Good times with kindred spirits have carried you far.",
      present: "Now is a time to gather, toast, and share joy with others.",
      future: "A reason to celebrate is near — you won't be alone in it.",
    },
    astrology: {
      zodiacSign: "Cancer",
      planet: "Mercury",
      element: "Water",
      modality: "Cardinal",
    },
    qabalah: { path: "Binah", hebrewLetter: null, gematria: null },
  },
  {
    id: 39,
    name: "Four of Cups",
    meaning: "Four of Cups: Meditation, contemplation, apathy, re-evaluation",
    reversedMeaning:
      "Reversed: Retreat, withdrawal, checking in for alignment, boredom, missed opportunity",
    theme: ["introspection", "apathy", "choice"],
    image: "/images/four_of_cups.jpg",
    narrative: {
      past: "Emotional fatigue or missed chances have weighed on you.",
      present:
        "Take time to look inward — what's being offered that you haven't seen?",
      future:
        "New opportunities will come — but only if you're ready to receive them.",
    },
    astrology: {
      zodiacSign: "Cancer",
      planet: "Moon",
      element: "Water",
      modality: "Cardinal",
    },
    qabalah: { path: "Chesed", hebrewLetter: null, gematria: null },
  },
  {
    id: 40,
    name: "Five of Cups",
    meaning: "Five of Cups: Loss, regret, disappointment, sadness",
    reversedMeaning:
      "Reversed: Personal setbacks, self-forgiveness, moving on, acceptance",
    theme: ["loss", "sadness", "reflection"],
    image: "/images/five_of_cups.jpg",
    narrative: {
      past: "You've experienced emotional pain — and the story still echoes.",
      present: "You may be focused on what's missing — but not all is lost.",
      future:
        "Healing is ahead. What you thought was gone may return in new form.",
    },
    astrology: {
      zodiacSign: "Scorpio",
      planet: "Mars",
      element: "Water",
      modality: "Fixed",
    },
    qabalah: { path: "Geburah", hebrewLetter: null, gematria: null },
  },
  {
    id: 41,
    name: "Six of Cups",
    meaning:
      "Six of Cups: Revisiting the past, childhood memories, innocence, joy",
    reversedMeaning:
      "Reversed: Living in the past, forgiveness, lacking playfulness, maturity",
    theme: ["nostalgia", "joy", "innocence"],
    image: "/images/six_of_cups.jpg",
    narrative: {
      past: "You've been shaped by deep bonds and sweet memories — they still influence you.",
      present:
        "A moment of nostalgia or reconnection is surfacing — let it soften you.",
      future:
        "Someone or something from your past may return — offering peace or closure.",
    },
    astrology: {
      zodiacSign: "Scorpio",
      planet: "Sun",
      element: "Water",
      modality: "Fixed",
    },
    qabalah: { path: "Tiphareth", hebrewLetter: null, gematria: null },
  },
  {
    id: 42,
    name: "Seven of Cups",
    meaning:
      "Seven of Cups: Opportunities, choices, wishful thinking, illusion",
    reversedMeaning:
      "Reversed: Alignment, personal values, overwhelmed by choices, lack of purpose",
    theme: ["choice", "illusion", "potential"],
    image: "/images/seven_of_cups.jpg",
    narrative: {
      past: "You've been captivated by what could be — some real, some illusion.",
      present: "You're surrounded by options — but not all are what they seem.",
      future: "A decision will call for clarity — trust your inner compass.",
    },
    astrology: {
      zodiacSign: "Scorpio",
      planet: "Venus",
      element: "Water",
      modality: "Fixed",
    },
    qabalah: { path: "Netzach", hebrewLetter: null, gematria: null },
  },
  {
    id: 43,
    name: "Eight of Cups",
    meaning: "Eight of Cups: Disappointment, abandonment, withdrawal, escapism",
    reversedMeaning:
      "Reversed: Trying one more time, indecision, avoidance, fear of change, staying in a bad situation",
    theme: ["release", "transition", "sadness"],
    image: "/images/eight_of_cups.jpg",
    narrative: {
      past: "You've walked away from something you once cared about — that choice was necessary.",
      present: "Now is a time to leave behind what no longer fulfills you.",
      future: "A journey toward deeper truth is ahead — let go with grace.",
    },
    astrology: {
      zodiacSign: "Pisces",
      planet: "Saturn",
      element: "Water",
      modality: "Mutable",
    },
    qabalah: { path: "Hod", hebrewLetter: null, gematria: null },
  },
  {
    id: 44,
    name: "Nine of Cups",
    meaning:
      "Nine of Cups: Contentment, satisfaction, gratitude, wish come true",
    reversedMeaning:
      "Reversed: Inner happiness, dissatisfaction, materialism, lack of fulfillment, greed",
    theme: ["joy", "success", "gratitude"],
    image: "/images/nine_of_cups.jpg",
    narrative: {
      past: "You've tasted joy — the kind that filled you up from the inside.",
      present: "Now is a time to enjoy what you've built. You've earned this.",
      future: "A dream is on its way — your joy will be full.",
    },
    astrology: {
      zodiacSign: "Pisces",
      planet: "Jupiter",
      element: "Water",
      modality: "Mutable",
    },
    qabalah: { path: "Yesod", hebrewLetter: null, gematria: null },
  },
  {
    id: 45,
    name: "Ten of Cups",
    meaning:
      "Ten of Cups: Divine love, blissful relationships, harmony, alignment",
    reversedMeaning:
      "Reversed: Disconnection, misaligned values, struggling relationships, lack of harmony",
    theme: ["love", "completion", "joy"],
    image: "/images/ten_of_cups.jpg",
    narrative: {
      past: "You've known emotional completion — that feeling still guides your ideals.",
      present:
        "You're in or nearing a time of great emotional peace — savor it.",
      future: "Emotional wholeness and lasting harmony are coming into view.",
    },
    astrology: {
      zodiacSign: "Pisces",
      planet: "Mars",
      element: "Water",
      modality: "Mutable",
    },
    qabalah: { path: "Malkuth", hebrewLetter: null, gematria: null },
  },
  {
    id: 46,
    name: "Page of Cups",
    meaning:
      "Page of Cups: Creative opportunities, intuition, curiosity, possibility",
    reversedMeaning:
      "Reversed: New ideas, doubting intuition, creative blocks, emotional immaturity, insecurity",
    theme: ["creativity", "intuition", "beginnings"],
    image: "/images/page_of_cups.jpg",
    narrative: {
      past: "Your sensitivity once opened you to deep emotional insights — and it's still your gift.",
      present:
        "Now is a time to trust your intuition and be open to messages from the heart.",
      future:
        "A surprise connection or creative message is coming — stay soft and curious.",
    },
    astrology: {
      zodiacSign: null,
      planet: null,
      element: "Earth of Water",
      modality: null,
    },
    qabalah: { path: null, hebrewLetter: null, gematria: null },
  },
  {
    id: 47,
    name: "Knight of Cups",
    meaning: "Knight of Cups: Creativity, romance, charm, imagination, beauty",
    reversedMeaning:
      "Reversed: Overactive imagination, unrealistic, jealousy, moodiness, disappointment",
    theme: ["romance", "creativity", "action"],
    image: "/images/knight_of_cups.jpg",
    narrative: {
      past: "You've followed your heart before — even when it led to unknown places.",
      present:
        "Now you're being guided by emotion — let your heart lead but keep your eyes open.",
      future:
        "A heartfelt offer or romantic path may soon emerge — trust the journey.",
    },
    astrology: {
      zodiacSign: null,
      planet: null,
      element: "Air of Water",
      modality: null,
    },
    qabalah: { path: null, hebrewLetter: null, gematria: null },
  },
  {
    id: 48,
    name: "Queen of Cups",
    meaning: "Queen of Cups: Compassion, calm, intuition, flow",
    reversedMeaning:
      "Reversed: Inner feelings, self-care, self-love, co-dependency, emotional insecurity",
    theme: ["intuition", "nurturing", "love"],
    image: "/images/queen_of_cups.jpg",
    narrative: {
      past: "You've held space for others' emotions — and developed strength in softness.",
      present:
        "Now is the time to honor your emotional wisdom and care deeply for yourself and others.",
      future:
        "Your capacity for compassion will be needed — and it will guide healing.",
    },
    astrology: {
      zodiacSign: null,
      planet: null,
      element: "Water of Water",
      modality: null,
    },
    qabalah: { path: null, hebrewLetter: null, gematria: null },
  },
  {
    id: 49,
    name: "King of Cups",
    meaning: "King of Cups: Emotionally balanced, compassionate, diplomatic",
    reversedMeaning:
      "Reversed: Self-compassion, inner feelings, moodiness, emotional manipulation, volatility",
    theme: ["balance", "wisdom", "love"],
    image: "/images/king_of_cups.jpg",
    narrative: {
      past: "You've learned to steady your emotions — even amidst turbulence.",
      present:
        "Lead with empathy now. Your emotional mastery is your strength.",
      future:
        "A situation will call for wisdom of the heart — remain calm and centered.",
    },
    astrology: {
      zodiacSign: null,
      planet: null,
      element: "Fire of Water",
      modality: null,
    },
    qabalah: { path: null, hebrewLetter: null, gematria: null },
  },

  // Suit of Swords (50-63)
  {
    id: 50,
    name: "Ace of Swords",
    meaning: "Ace of Swords: Breakthrough, clarity, truth, new ideas",
    reversedMeaning:
      "Reversed: Inner clarity, re-thinking an idea, clouded judgement, confusion, chaos",
    theme: ["truth", "clarity", "beginnings"],
    image: "/images/ace_of_swords.jpg",
    narrative: {
      past: "You've had a moment of truth — and it changed everything.",
      present: "Now is a time of clarity — speak and act with conviction.",
      future: "A breakthrough is coming — sharpen your mind and stay alert.",
    },
    astrology: {
      zodiacSign: null,
      planet: null,
      element: "Air",
      modality: null,
    },
    qabalah: { path: "Kether", hebrewLetter: null, gematria: null },
  },
  {
    id: 51,
    name: "Two of Swords",
    meaning: "Two of Swords: Indecision, choice, avoidance, stalemate",
    reversedMeaning:
      "Reversed: Indecision, confusion, information overload, stalemate, anxiety",
    theme: ["choice", "balance", "uncertainty"],
    image: "/images/two_of_swords.jpg",
    narrative: {
      past: "You've delayed decisions before — fear of wrong steps kept you still.",
      present:
        "You're caught between options — take time, but don't avoid the truth.",
      future:
        "A choice will demand clarity — trust your judgment over comfort.",
    },
    astrology: {
      zodiacSign: "Libra",
      planet: "Moon",
      element: "Air",
      modality: "Cardinal",
    },
    qabalah: { path: "Chokmah", hebrewLetter: null, gematria: null },
  },
  {
    id: 52,
    name: "Three of Swords",
    meaning: "Three of Swords: Heartbreak, betrayal, sorrow, grief",
    reversedMeaning:
      "Reversed: Negative self-talk, releasing pain, optimism, forgiveness, recovery",
    theme: ["loss", "pain", "truth"],
    image: "/images/three_of_swords.jpg",
    narrative: {
      past: "Heartache once cut deep — but you're still standing.",
      present:
        "Now is a time of emotional honesty — let yourself feel to begin healing.",
      future: "Grief may arise, but clarity and growth will follow.",
    },
    astrology: {
      zodiacSign: "Libra",
      planet: "Saturn",
      element: "Air",
      modality: "Cardinal",
    },
    qabalah: { path: "Binah", hebrewLetter: null, gematria: null },
  },
  {
    id: 53,
    name: "Four of Swords",
    meaning: "Four of Swords: Rest, recovery, reflection, meditation",
    reversedMeaning:
      "Reversed: Exhaustion, burn-out, deep contemplation, stagnation, restlessness",
    theme: ["rest", "introspection", "healing"],
    image: "/images/four_of_swords.jpg",
    narrative: {
      past: "You've needed rest before — recovery brought insight.",
      present:
        "This is a moment to pause — your spirit needs space to breathe.",
      future: "A chance to retreat and renew is coming — embrace the quiet.",
    },
    astrology: {
      zodiacSign: "Libra",
      planet: "Jupiter",
      element: "Air",
      modality: "Cardinal",
    },
    qabalah: { path: "Chesed", hebrewLetter: null, gematria: null },
  },
  {
    id: 54,
    name: "Five of Swords",
    meaning: "Five of Swords: Conflict, betrayal, ego, hollow victory",
    reversedMeaning:
      "Reversed: Reconciliation, making amends, past resentment, desire to forgive",
    theme: ["conflict", "ego", "tension"],
    image: "/images/five_of_swords.jpg",
    narrative: {
      past: "You've seen what happens when pride wins — but the cost lingers.",
      present:
        "You may be caught in conflict — ask what's truly worth fighting for.",
      future: "A test of ethics is coming — choose wisdom over winning.",
    },
    astrology: {
      zodiacSign: "Aquarius",
      planet: "Venus",
      element: "Air",
      modality: "Fixed",
    },
    qabalah: { path: "Geburah", hebrewLetter: null, gematria: null },
  },
  {
    id: 55,
    name: "Six of Swords",
    meaning:
      "Six of Swords: Transition, rite of passage, moving on, healing journey",
    reversedMeaning:
      "Reversed: Spiritual transition, resistance to change, unfinished business, trapped",
    theme: ["transition", "healing", "passage"],
    image: "/images/six_of_swords.jpg",
    narrative: {
      past: "You’ve journeyed through stormy waters, slowly finding clarity.",
      present:
        "Now is a time of emotional or mental transition — you’re crossing into calmer space.",
      future:
        "Healing lies ahead. Keep moving forward and let the past drift away.",
    },
    astrology: {
      zodiacSign: "Aquarius",
      planet: "Mercury",
      element: "Air",
      modality: "Fixed",
    },
    qabalah: { path: 31, hebrewLetter: "Shin", gematria: 300 }, // Retained provided Qabalah
  },
  {
    id: 56,
    name: "Seven of Swords",
    meaning: "Seven of Swords: Deception, strategy, cunning, stealth",
    reversedMeaning:
      "Reversed: Imposter syndrome, deceit, keeping secrets, confession, conscience",
    theme: ["deception", "strategy", "independence"],
    image: "/images/seven_of_swords.jpg",
    narrative: {
      past: "You’ve taken unconventional routes — not all were honest, but some were necessary.",
      present:
        "There’s a need to act with caution and wit. Not all is as it seems.",
      future: "A clever move will be required — tread lightly and stay alert.",
    },
    astrology: {
      zodiacSign: "Aquarius",
      planet: "Moon",
      element: "Air",
      modality: "Fixed",
    },
    qabalah: { path: 32, hebrewLetter: "Tav", gematria: 400 }, // Retained provided Qabalah
  },
  {
    id: 57,
    name: "Eight of Swords",
    meaning:
      "Eight of Swords: Limitation, restriction, self-imposed traps, confusion",
    reversedMeaning:
      "Reversed: Self-limiting beliefs, inner critic, releasing negative thoughts, open to new perspectives",
    theme: ["restriction", "fear", "mental block"],
    image: "/images/eight_of_swords.jpg",
    narrative: {
      past: "You’ve felt bound by circumstance or belief — and it held you back.",
      present: "You may feel trapped — but the key to release lies within you.",
      future:
        "A breakthrough is coming — clarity will dissolve these illusions.",
    },
    astrology: {
      zodiacSign: "Gemini",
      planet: "Jupiter",
      element: "Air",
      modality: "Mutable",
    },
    qabalah: { path: 25, hebrewLetter: "Samekh", gematria: 60 }, // Retained provided Qabalah
  },
  {
    id: 58,
    name: "Nine of Swords",
    meaning: "Nine of Swords: Anxiety, nightmares, worry, mental anguish",
    reversedMeaning:
      "Reversed: Inner turmoil, deep-seated fears, secrets, releasing worry, seeking help",
    theme: ["anxiety", "guilt", "regret"],
    image: "/images/nine_of_swords.jpg",
    narrative: {
      past: "You’ve struggled with inner demons — sleepless nights and spiraling thought.",
      present:
        "Anxiety may be high — but know you’re not alone. Speak your truth.",
      future: "Relief is possible. Face what haunts you to dissolve its hold.",
    },
    astrology: {
      zodiacSign: "Gemini",
      planet: "Mars",
      element: "Air",
      modality: "Mutable",
    },
    qabalah: { path: 26, hebrewLetter: "Ayin", gematria: 70 }, // Retained provided Qabalah
  },
  {
    id: 59,
    name: "Ten of Swords",
    meaning: "Ten of Swords: Betrayal, defeat, painful ending, rock bottom",
    reversedMeaning:
      "Reversed: Recovery, regeneration, resisting an inevitable end, survival, release",
    theme: ["ending", "sacrifice", "closure"],
    image: "/images/ten_of_swords.jpg",
    narrative: {
      past: "You’ve experienced a painful conclusion — one that reshaped everything.",
      present:
        "It may feel like the worst is happening — but this is the end of the cycle.",
      future: "A painful truth will be released — and clarity will return.",
    },
    astrology: {
      zodiacSign: "Gemini",
      planet: "Sun",
      element: "Air",
      modality: "Mutable",
    },
    qabalah: { path: 27, hebrewLetter: "Peh", gematria: 80 }, // Retained provided Qabalah
  },
  {
    id: 60,
    name: "Page of Swords",
    meaning:
      "Page of Swords: New ideas, curiosity, thirst for knowledge, energy",
    reversedMeaning:
      "Reversed: Self-expression, all talk and no action, haste, scattered energy, cynicism",
    theme: ["curiosity", "truth", "energy"],
    image: "/images/page_of_swords.jpg",
    narrative: {
      past: "You've questioned and observed before — and those insights still guide you.",
      present:
        "Your mind is alert — now is a great time to learn, explore, or speak up.",
      future: "A message or fresh idea will spark movement — be ready to act.",
    },
    astrology: {
      zodiacSign: null,
      planet: null,
      element: "Earth of Air",
      modality: null,
    },
    qabalah: { path: null, hebrewLetter: null, gematria: null },
  },
  {
    id: 61,
    name: "Knight of Swords",
    meaning:
      "Knight of Swords: Ambitious, action-oriented, driven to succeed, fast-thinking",
    reversedMeaning:
      "Reversed: Restless, unfocused, impulsive, burn-out, aggressive",
    theme: ["action", "determination", "speed"],
    image: "/images/knight_of_swords.jpg",
    narrative: {
      past: "You've rushed forward in pursuit of something — and learned from the speed.",
      present:
        "You may feel an urgency to act — direct that power with precision.",
      future:
        "A call to take swift action will come — stay sharp and intentional.",
    },
    astrology: {
      zodiacSign: null,
      planet: null,
      element: "Air of Air",
      modality: null,
    },
    qabalah: { path: null, hebrewLetter: null, gematria: null },
  },
  {
    id: 62,
    name: "Queen of Swords",
    meaning:
      "Queen of Swords: Independent, unbiased judgement, clear boundaries, direct communication",
    reversedMeaning:
      "Reversed: Overly emotional, easily influenced, bitchy, cold-hearted, cruel",
    theme: ["independence", "truth", "clarity"],
    image: "/images/queen_of_swords.jpg",
    narrative: {
      past: "You've cut through illusions before — and your honesty shaped you.",
      present:
        "Now is a time to speak your truth with compassion and firmness.",
      future:
        "A decision will call for your clearest thinking — your mind will guide you well.",
    },
    astrology: {
      zodiacSign: null,
      planet: null,
      element: "Water of Air",
      modality: null,
    },
    qabalah: { path: null, hebrewLetter: null, gematria: null },
  },
  {
    id: 63,
    name: "King of Swords",
    meaning:
      "King of Swords: Mental clarity, intellectual power, authority, truth",
    reversedMeaning:
      "Reversed: Quiet power, inner truth, misuse of power, manipulation, tyrannical",
    theme: ["authority", "truth", "logic"],
    image: "/images/king_of_swords.jpg",
    narrative: {
      past: "You've led with your mind before — reason has long been your ally.",
      present:
        "Use your intellect now to lead, decide, and act with integrity.",
      future:
        "A moment requiring wisdom and fairness is coming — be prepared to lead with clarity.",
    },
    astrology: {
      zodiacSign: null,
      planet: null,
      element: "Fire of Air",
      modality: null,
    },
    qabalah: { path: null, hebrewLetter: null, gematria: null },
  },

  // Suit of Pentacles (64-77)
  {
    id: 64,
    name: "Ace of Pentacles",
    meaning:
      "Ace of Pentacles: Manifestation, new financial opportunity, prosperity",
    reversedMeaning:
      "Reversed: Lost opportunity, lack of planning, missed chance, poor financial control",
    theme: ["beginnings", "manifestation", "abundance"],
    image: "/images/ace_of_pentacles.jpg",
    narrative: {
      past: "You were offered a seed of opportunity — and its roots still grow within you.",
      present: "A tangible gift or chance is here — plant it well.",
      future:
        "Abundance is near. Nurture the ground beneath you, and it will flourish.",
    },
    astrology: {
      zodiacSign: null,
      planet: null,
      element: "Earth",
      modality: null,
    },
    qabalah: { path: "Kether", hebrewLetter: null, gematria: null },
  },
  {
    id: 65,
    name: "Two of Pentacles",
    meaning:
      "Two of Pentacles: Balance, adaptability, time management, prioritization",
    reversedMeaning:
      "Reversed: Overcommitted, disorganisation, reprioritising, imbalance, poor time management",
    theme: ["balance", "change", "cycles"],
    image: "/images/two_of_pentacles.jpg",
    narrative: {
      past: "You've been pulled between responsibilities — juggling what mattered most.",
      present: "You are managing many roles — stay grounded and flexible.",
      future:
        "A shift may require you to adjust quickly — balance will be your superpower.",
    },
    astrology: {
      zodiacSign: "Capricorn",
      planet: "Jupiter",
      element: "Earth",
      modality: "Cardinal",
    },
    qabalah: { path: "Chokmah", hebrewLetter: null, gematria: null },
  },
  {
    id: 66,
    name: "Three of Pentacles",
    meaning:
      "Three of Pentacles: Teamwork, collaboration, learning, implementation",
    reversedMeaning:
      "Reversed: Disharmony, misalignment, working alone, lack of teamwork, poor quality",
    theme: ["collaboration", "growth", "skill"],
    image: "/images/three_of_pentacles.jpg",
    narrative: {
      past: "You laid a strong foundation through effort and cooperation.",
      present:
        "Now is the time to build with others. Shared vision brings strength.",
      future: "Support and teamwork will elevate your next creation.",
    },
    astrology: {
      zodiacSign: "Capricorn",
      planet: "Mars",
      element: "Earth",
      modality: "Cardinal",
    },
    qabalah: { path: "Binah", hebrewLetter: null, gematria: null },
  },
  {
    id: 67,
    name: "Four of Pentacles",
    meaning:
      "Four of Pentacles: Control, stability, security, possession, conservatism",
    reversedMeaning:
      "Reversed: Over-spending, greed, self-protection, possessiveness, materialism",
    theme: ["stability", "control", "materialism"],
    image: "/images/four_of_pentacles.jpg",
    narrative: {
      past: "You've held tightly to what mattered — sometimes out of fear.",
      present: "You're being asked to examine what you cling to — and why.",
      future:
        "A moment will arise where release brings greater peace than possession.",
    },
    astrology: {
      zodiacSign: "Capricorn",
      planet: "Sun",
      element: "Earth",
      modality: "Cardinal",
    },
    qabalah: { path: "Chesed", hebrewLetter: null, gematria: null },
  },
  {
    id: 68,
    name: "Five of Pentacles",
    meaning: "Five of Pentacles: Financial loss, poverty, isolation, worry",
    reversedMeaning:
      "Reversed: Recovery from financial loss, spiritual poverty, isolation, positive change",
    theme: ["scarcity", "loss", "isolation"],
    image: "/images/five_of_pentacles.jpg",
    narrative: {
      past: "You've endured lack or uncertainty — and survived.",
      present:
        "This may feel like a lonely or unstable time. Look for light in unexpected places.",
      future: "Support is on its way — you are not alone in this chapter.",
    },
    astrology: {
      zodiacSign: "Taurus",
      planet: "Mercury",
      element: "Earth",
      modality: "Fixed",
    },
    qabalah: { path: "Geburah", hebrewLetter: null, gematria: null },
  },
  {
    id: 69,
    name: "Six of Pentacles",
    meaning:
      "Six of Pentacles: Giving, receiving, sharing wealth, generosity, charity",
    reversedMeaning:
      "Reversed: Self-care, unpaid debts, one-sided charity, stinginess",
    theme: ["generosity", "balance", "abundance"],
    image: "/images/six_of_pentacles.jpg",
    narrative: {
      past: "You've known both the joy of giving and the humility of receiving.",
      present: "Now is a time to notice the flow of generosity around you.",
      future:
        "An act of kindness will ripple back to you — or through you to others.",
    },
    astrology: {
      zodiacSign: "Taurus",
      planet: "Moon",
      element: "Earth",
      modality: "Fixed",
    },
    qabalah: { path: "Tiphareth", hebrewLetter: null, gematria: null },
  },
  {
    id: 70,
    name: "Seven of Pentacles",
    meaning:
      "Seven of Pentacles: Long-term view, sustainable results, perseverance, investment",
    reversedMeaning:
      "Reversed: Lack of long-term vision, limited success, procrastination, impatience",
    theme: ["patience", "growth", "investment"],
    image: "/images/seven_of_pentacles.jpg",
    narrative: {
      past: "You've planted something meaningful — and waited with care.",
      present:
        "Now is the time to evaluate progress. Trust what's quietly growing.",
      future: "Your efforts will bear fruit — keep nurturing the process.",
    },
    astrology: {
      zodiacSign: "Taurus",
      planet: "Saturn",
      element: "Earth",
      modality: "Fixed",
    },
    qabalah: { path: "Netzach", hebrewLetter: null, gematria: null },
  },
  {
    id: 71,
    name: "Eight of Pentacles",
    meaning:
      "Eight of Pentacles: Apprenticeship, repetitive tasks, mastery, skill development",
    reversedMeaning:
      "Reversed: Self-development, perfectionism, misdirected activity, lack of focus",
    theme: ["skill", "patience", "mastery"],
    image: "/images/eight_of_pentacles.jpg",
    narrative: {
      past: "You've devoted yourself to honing your craft — and it shows.",
      present:
        "Now is the time to keep building skill and precision — mastery is near.",
      future:
        "Your focus will lead to excellence — and your work will speak for itself.",
    },
    astrology: {
      zodiacSign: "Virgo",
      planet: "Sun",
      element: "Earth",
      modality: "Mutable",
    },
    qabalah: { path: "Hod", hebrewLetter: null, gematria: null },
  },
  {
    id: 72,
    name: "Nine of Pentacles",
    meaning:
      "Nine of Pentacles: Abundance, luxury, self-sufficiency, financial independence",
    reversedMeaning:
      "Reversed: Financial dependency, superficiality, reckless spending, living beyond means",
    theme: ["abundance", "independence", "success"],
    image: "/images/nine_of_pentacles.jpg",
    narrative: {
      past: "You've built something of your own — and it continues to support you.",
      present:
        "Now is a time to enjoy the fruits of your labor. Savor the comfort you've earned.",
      future:
        "A moment of peace and prosperity is coming — and you'll have created it.",
    },
    astrology: {
      zodiacSign: "Virgo",
      planet: "Venus",
      element: "Earth",
      modality: "Mutable",
    },
    qabalah: { path: "Yesod", hebrewLetter: null, gematria: null },
  },
  {
    id: 73,
    name: "Ten of Pentacles",
    meaning:
      "Ten of Pentacles: Wealth, family, legacy, inheritance, culmination",
    reversedMeaning:
      "Reversed: Fleeting success, financial failure, family disputes, burden, instability",
    theme: ["completion", "abundance", "legacy"],
    image: "/images/ten_of_pentacles.jpg",
    narrative: {
      past: "You've been supported by deep roots — or longed for them.",
      present:
        "Now is a time to build something that lasts — for you and those who follow.",
      future:
        "A legacy is forming. What you create now may echo beyond your own lifetime.",
    },
    astrology: {
      zodiacSign: "Virgo",
      planet: "Mercury",
      element: "Earth",
      modality: "Mutable",
    },
    qabalah: { path: "Malkuth", hebrewLetter: null, gematria: null },
  },
  {
    id: 74,
    name: "Page of Pentacles",
    meaning:
      "Page of Pentacles: Manifestation, financial opportunity, skill development",
    reversedMeaning:
      "Reversed: Lack of progress, procrastination, learn from failure, laziness",
    theme: ["beginnings", "manifestation", "learning"],
    image: "/images/page_of_pentacles.jpg",
    narrative: {
      past: "You once committed to learning or building something tangible — that dedication continues to reward you.",
      present:
        "A new skill or opportunity is calling — stay grounded and focused.",
      future: "Study or persistence will soon pay off in a tangible way.",
    },
    astrology: {
      zodiacSign: null,
      planet: null,
      element: "Earth of Earth",
      modality: null,
    },
    qabalah: { path: null, hebrewLetter: null, gematria: null },
  },
  {
    id: 75,
    name: "Knight of Pentacles",
    meaning:
      "Knight of Pentacles: Hard work, productivity, routine, conservatism",
    reversedMeaning:
      "Reversed: Self-discipline, boredom, feeling 'stuck', perfectionism, laziness",
    theme: ["patience", "stability", "determination"],
    image: "/images/knight_of_pentacles.jpg",
    narrative: {
      past: "Your slow and steady approach laid a lasting foundation.",
      present:
        "Now is a time for dependable action — keep going, even if it's not glamorous.",
      future: "Persistence will lead to long-term reward — stay the course.",
    },
    astrology: {
      zodiacSign: null,
      planet: null,
      element: "Air of Earth",
      modality: null,
    },
    qabalah: { path: null, hebrewLetter: null, gematria: null },
  },
  {
    id: 76,
    name: "Queen of Pentacles",
    meaning:
      "Queen of Pentacles: Nurturing, practical, providing financially, luxury",
    reversedMeaning:
      "Reversed: Financial independence, self-care, work-home conflict, smothering",
    theme: ["nurturing", "abundance", "stability"],
    image: "/images/queen_of_pentacles.jpg",
    narrative: {
      past: "You've created safety for others — or sought it deeply for yourself.",
      present:
        "Now is the time to blend practicality with compassion — both can thrive.",
      future:
        "A role of support, success, or stewardship will soon emerge — and you'll shine in it.",
    },
    astrology: {
      zodiacSign: null,
      planet: null,
      element: "Water of Earth",
      modality: null,
    },
    qabalah: { path: null, hebrewLetter: null, gematria: null },
  },
  {
    id: 77,
    name: "King of Pentacles",
    meaning:
      "King of Pentacles: Wealth, business, leadership, security, discipline",
    reversedMeaning:
      "Reversed: Authoritarian, domineering, materialistic, corrupt, instability",
    theme: ["abundance", "power", "stability"],
    image: "/images/king_of_pentacles.jpg",
    narrative: {
      past: "You've built something that lasts — and others benefit from your strength.",
      present:
        "Step into a role of leadership or prosperity — this is your season of stability.",
      future:
        "Security and accomplishment are forming — the foundation is solid beneath you.",
    },
    astrology: {
      zodiacSign: null,
      planet: null,
      element: "Fire of Earth",
      modality: null,
    },
    qabalah: { path: null, hebrewLetter: null, gematria: null },
  },
];

export default tarotCards;
